import ExcelJS from 'exceljs'
import { formatDate } from '../../../../Assets/Helpers'

const getPerfsFromData = (data) => {
    const perfs = []
    for (const el of data) {
        if (el?.Parameters) {
            for (const parameter of el.Parameters) {
                if (parameter.Type === 'out' && parameter.Values?.[0]?.Status?.[0]?.CalcType) {
                    const existingPerf = perfs.find((perf) => perf.name === parameter.Name)
                    if (!existingPerf) {
                        perfs.push({ name: parameter.Name, unit: parameter.Unit || '' })
                    }
                }
            }
        }
    }
    return perfs
}

const getLatestCalculationDateForCandidate = (candidate, calcType) => {
    let latestTimestamp = 0
    let calcStatus = ''

    if (candidate?.Parameters) {
        for (const parameter of candidate.Parameters) {
            if (parameter.Type === 'out' && parameter.Values) {
                for (const value of parameter.Values) {
                    if (value.Status) {
                        for (const status of value.Status) {
                            if (status.CalcType?.trim() === calcType.trim() && status.Date) {
                                const timestamp = status.Date
                                if (timestamp > latestTimestamp) {
                                    latestTimestamp = timestamp
                                    calcStatus = status.CalcStatus || ''
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    if (latestTimestamp > 0) {
        const date = new Date(latestTimestamp)
        if (isNaN(date.getTime())) {
            return { formatted: '', timestamp: 0, calcStatus: '' }
        }

        return {
            formatted: date.toLocaleString('en-CA'),
            timestamp: latestTimestamp,
            calcStatus: calcStatus,
        }
    }

    return { formatted: '', timestamp: 0, calcStatus: '' }
}
const hasDataForColumn = (data, columnName, columnType, calcType) => {
    if (!data || !Array.isArray(data) || data.length === 0) return false

    return data.some((candidate) => {
        if (!candidate?.Parameters || !Array.isArray(candidate.Parameters)) return false

        const parameter = candidate.Parameters.find((param) => {
            if (columnType === 'option') {
                return param.Description === columnName && param.Type === 'in'
            } else if (columnType === 'performance') {
                if (param.Name === columnName && param.Type === 'out' && param.Values) {
                    return param.Values.some((value) =>
                        value.Status?.some((status) => status.CalcType?.trim() === calcType.trim())
                    )
                }
            }
            return false
        })

        if (columnType === 'option') {
            if (!parameter || !parameter.Values?.[0]) return false
            const value = parameter.Values[0].Value
            return value !== null && value !== undefined && value !== ''
        }

        return !!parameter
    })
}

const hasPerformanceDataForSheet = (data, calcType, perfsList) => {
    if (!data || !Array.isArray(data) || data.length === 0) return false
    if (!perfsList || !Array.isArray(perfsList) || perfsList.length === 0) return false

    return data.some((candidate) => {
        if (!candidate?.Parameters || !Array.isArray(candidate.Parameters)) return false

        return perfsList.some((perfName) => {
            const parameter = candidate.Parameters.find(
                (param) =>
                    param.Name === perfName &&
                    param.Type === 'out' &&
                    param.Values?.[0]?.Status?.[0]?.CalcType?.trim() === calcType
            )
            return !!parameter && !!parameter.Values?.[0]?.Status?.[0]
        })
    })
}
const hasDataForSheet = (data, calcType, perfsList) => {
    if (!data || !Array.isArray(data) || data.length === 0) return false

    // Check if there are any candidates with input parameters
    const hasInputData = data.some((candidate) => {
        if (!candidate?.Parameters || !Array.isArray(candidate.Parameters)) return false
        return candidate.Parameters.some((param) => param.Type === 'in')
    })

    // Check if there are any performance data for this calcType
    const hasPerformanceData = hasPerformanceDataForSheet(data, calcType, perfsList)

    // Only include sheet if it has both input data AND performance data
    return hasInputData && hasPerformanceData
}

const convertToNumber = (value) => {
    if (value === null || value === undefined || value === '') {
        return value
    }

    const numValue = parseFloat(value)
    if (!isNaN(numValue) && isFinite(numValue)) {
        return numValue
    }

    return value
}

const isFloat = (value) => {
    const numValue = parseFloat(value)
    return !isNaN(numValue) && isFinite(numValue) && numValue % 1 !== 0
}

const generateExcelFile = ({ model, calcTypes, data }) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
        console.warn('No data provided to generate Excel file')
        return
    }

    if (!calcTypes || !Array.isArray(calcTypes) || calcTypes.length === 0) {
        console.warn('No calculation types provided')
        return
    }

    const allInputParameters = data[0]?.Parameters?.filter((parameter) => parameter.Type === 'in') || []
    const sizeParameter = allInputParameters.find((param) => param.Description?.toLowerCase() === 'size')
    const otherParameters = allInputParameters.filter((param) => param.Description?.toLowerCase() !== 'size')

    const sortedOtherOptions = otherParameters
        .filter((opt) => opt.Description)
        .sort((a, b) => a.Description.localeCompare(b.Description))
        .map((opt) => opt.Description)

    const allOptions = sizeParameter ? [sizeParameter.Description, ...sortedOtherOptions] : sortedOtherOptions

    const allPerfs = getPerfsFromData(data)
    const uniquePerfs = Array.from(new Set(allPerfs.map((obj) => obj.name))).map((name) =>
        allPerfs.find((obj) => obj.name === name)
    )

    const sortedUniquePerfs = uniquePerfs
        .filter((perf) => perf && perf.name)
        .sort((a, b) => a.name.localeCompare(b.name))
    const allPerfsList = sortedUniquePerfs.map((perf) => perf.name)

    const workbook = new ExcelJS.Workbook()
    let sheets = []

    // Filter calcTypes to only include those with performance data
    const validCalcTypes = calcTypes.filter((calcType) => {
        return calcType && hasDataForSheet(data, calcType, allPerfsList)
    })

    if (validCalcTypes.length === 0) {
        console.warn('No valid calculation types with performance data found')
        return
    }

    // Only create sheets for calcTypes that have performance data
    validCalcTypes.forEach((calculation) => {
        try {
            sheets.push(workbook.addWorksheet(calculation))
        } catch (error) {
            console.warn(`Failed to create worksheet for ${calculation}:`, error)
        }
    })

    if (sheets.length === 0) {
        console.warn('No sheets could be created')
        return
    }

    sheets.forEach((sheet) => {
        try {
            const calcType = sheet.name

            // Filter options to only include those with data (calcType doesn't matter for input parameters)
            const optionsList = allOptions.filter((option) => hasDataForColumn(data, option, 'option', calcType))

            // Filter performances to only include those with data for this SPECIFIC calcType
            const perfsList = allPerfsList.filter((perf) => hasDataForColumn(data, perf, 'performance', calcType))

            const unitsList = perfsList.map((perfName) => {
                const perfObj = sortedUniquePerfs.find((perf) => perf.name === perfName)
                return perfObj ? perfObj.unit || '' : ''
            })

            // Skip sheet if no valid columns (this shouldn't happen due to filtering above, but safety check)
            if (optionsList.length === 0 && perfsList.length === 0) {
                console.warn(`No valid columns found for calcType: ${calcType}`)
                return
            }

            const performancesStartCol = optionsList.length + 1
            const totalColumns = optionsList.length + 2 + perfsList.length // +2 for CalcStatus and Date columns

            const titleRowData = new Array(totalColumns).fill('')
            titleRowData[0] = 'CANDIDATES DESCRIPTION'
            if (performancesStartCol <= totalColumns && perfsList.length > 0) {
                titleRowData[performancesStartCol - 1] = 'PERFORMANCES' // -1 because array is 0-indexed
            }

            const candidateTitle = sheet.addRow(titleRowData)

            if (optionsList.length > 1) {
                sheet.mergeCells(1, 1, 1, optionsList.length)
            }

            if (perfsList.length > 0) {
                const performancesEndCol = totalColumns
                if (performancesEndCol > performancesStartCol) {
                    sheet.mergeCells(1, performancesStartCol, 1, performancesEndCol)
                }
            }

            candidateTitle.font = { family: 4, size: 10, bold: true }
            candidateTitle.alignment = { horizontal: 'center' }

            const candidateCell = sheet.getCell('A1')
            if (candidateCell) {
                candidateCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'fde34b' },
                }
            }

            if (perfsList.length > 0) {
                const performanceCell = sheet.getCell(1, performancesStartCol)
                if (performanceCell) {
                    performanceCell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: '3f51b5' },
                    }
                }
            }

            // Add CalcStatus and Date columns before performance columns (only if there are performance columns)
            const headerRow = [...optionsList]
            if (perfsList.length > 0) {
                headerRow.push('CalcStatus', 'Date', ...perfsList)
            }
            const optionsRow = sheet.addRow(headerRow)

            const unitsRowData = [...optionsList.map(() => '')]
            if (perfsList.length > 0) {
                unitsRowData.push('', '', ...unitsList)
            }
            const unitsRow = sheet.addRow(unitsRowData)

            if (unitsRow) {
                unitsRow.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'ffffff' },
                }
                unitsRow.border = {
                    top: { style: 'thin', color: { argb: 'eceff1' } },
                    left: { style: 'thin', color: { argb: 'eceff1' } },
                    bottom: { style: 'thin', color: { argb: 'eceff1' } },
                    right: { style: 'thin', color: { argb: 'eceff1' } },
                }
            }

            if (optionsRow) {
                optionsRow.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: '0ab624' },
                }
            }

            // Prepare and sort candidates by size
            const candidatesWithData = []

            data?.forEach((candidate, index) => {
                if (!candidate?.Parameters || !Array.isArray(candidate.Parameters)) {
                    return
                }

                const { Parameters } = candidate

                // Always get input parameter values (for candidate description)
                const optionValues = optionsList.map((option) => {
                    const parameter = Parameters.find((parameter) => parameter.Description === option)
                    return parameter?.Values?.[0]?.Value || ''
                })

                // Check if this candidate has any input parameters
                const hasInputData = optionValues.some((value) => value !== '')
                if (!hasInputData) {
                    return // Skip candidates without input data
                }

                // Get size value for sorting
                const sizeValue = sizeParameter
                    ? Parameters.find((param) => param.Description === sizeParameter.Description)?.Values?.[0]?.Value ||
                      ''
                    : ''

                // Get latest calculation date for this specific candidate and calcType
                const latestDate = getLatestCalculationDateForCandidate(candidate, calcType)

                // Get performance values (regardless of success/failure) but only for this calcType
                const isSuccessful = latestDate.calcStatus === 'Success'
                const perfsValues = perfsList.map((perf) => {
                    const parameter = Parameters.find(
                        (parameter) =>
                            parameter.Name === perf &&
                            parameter.Values?.[0]?.Status?.[0]?.CalcType?.trim() === calcType &&
                            parameter.Values[0].Status[0].Date === latestDate.timestamp
                    )
                    return parameter?.Values?.[0]?.Value || ''
                })

                candidatesWithData.push({
                    candidate,
                    optionValues,
                    sizeValue,
                    latestDate,
                    isSuccessful,
                    perfsValues,
                })
            })

            // Sort candidates by size
            candidatesWithData.sort((a, b) => {
                const sizeA = a.sizeValue
                const sizeB = b.sizeValue

                // Try to parse as numbers first
                const numA = parseFloat(sizeA)
                const numB = parseFloat(sizeB)

                if (!isNaN(numA) && !isNaN(numB)) {
                    return numA - numB // Numeric sort
                }

                // If not numbers, sort alphabetically
                return sizeA.toString().localeCompare(sizeB.toString())
            })

            // Process sorted candidates
            let processedRows = 0
            candidatesWithData.forEach(({ candidate, optionValues, latestDate, isSuccessful, perfsValues }) => {
                // Build row data based on what columns exist
                const rowData = [...optionValues]
                if (perfsList.length > 0) {
                    rowData.push(latestDate.calcStatus || '', latestDate.formatted || '', ...perfsValues)
                }

                const row = sheet.addRow(rowData)
                processedRows++

                optionValues.forEach((value, optionIndex) => {
                    const cell = row.getCell(optionIndex + 1)
                    if (!cell) return

                    if (value === 'TRUE') {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: '4CAF50' },
                        }
                    } else if (value === 'FALSE') {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'F44336' },
                        }
                    } else {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: processedRows % 2 === 1 ? 'f6f8fa' : 'ffffff' },
                        }
                    }

                    cell.border = {
                        top: { style: 'thin', color: { argb: 'eceff1' } },
                        left: { style: 'thin', color: { argb: 'eceff1' } },
                        bottom: { style: 'thin', color: { argb: 'eceff1' } },
                        right: { style: 'thin', color: { argb: 'eceff1' } },
                    }
                })

                // Only style CalcStatus and Date cells if performance columns exist
                if (perfsList.length > 0) {
                    // Style CalcStatus cell: Bold text with colors, no background color
                    const calcStatusCell = row.getCell(optionsList.length + 1)
                    if (calcStatusCell) {
                        if (latestDate.calcStatus) {
                            const textColor = isSuccessful ? '4CAF50' : 'F44336' // Green for success, Red for failure
                            calcStatusCell.font = {
                                bold: true,
                                color: { argb: textColor },
                            }
                        }

                        calcStatusCell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: processedRows % 2 === 1 ? 'f6f8fa' : 'ffffff' }, // Standard alternating background
                        }
                        calcStatusCell.border = {
                            top: { style: 'thin', color: { argb: 'eceff1' } },
                            left: { style: 'thin', color: { argb: 'eceff1' } },
                            bottom: { style: 'thin', color: { argb: 'eceff1' } },
                            right: { style: 'thin', color: { argb: 'eceff1' } },
                        }
                    }

                    // Style Date cell
                    const dateCell = row.getCell(optionsList.length + 2)
                    if (dateCell) {
                        dateCell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: processedRows % 2 === 1 ? 'f6f8fa' : 'ffffff' },
                        }
                        dateCell.border = {
                            top: { style: 'thin', color: { argb: 'eceff1' } },
                            left: { style: 'thin', color: { argb: 'eceff1' } },
                            bottom: { style: 'thin', color: { argb: 'eceff1' } },
                            right: { style: 'thin', color: { argb: 'eceff1' } },
                        }
                    }

                    perfsValues.forEach((value, perfIndex) => {
                        const cell = row.getCell(optionsList.length + 3 + perfIndex) // +3 for CalcStatus and Date columns
                        if (!cell) return

                        const numericValue = convertToNumber(value)
                        cell.value = numericValue

                        if (isFloat(value)) {
                            cell.numFmt = '0.00'
                        }

                        // Standard alternating row colors for performance cells
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: processedRows % 2 === 1 ? 'f6f8fa' : 'ffffff' },
                        }

                        // Red text color for failed calculations (only if there's a value and calculation failed)
                        if (!isSuccessful && value !== '' && latestDate.calcStatus) {
                            cell.font = {
                                color: { argb: 'F44336' }, // Red text for failed values
                            }
                        }

                        cell.border = {
                            top: { style: 'thin', color: { argb: 'eceff1' } },
                            left: { style: 'thin', color: { argb: 'eceff1' } },
                            bottom: { style: 'thin', color: { argb: 'eceff1' } },
                            right: { style: 'thin', color: { argb: 'eceff1' } },
                        }
                    })
                }
            })

            if (processedRows > 0 && totalColumns > 0) {
                sheet.autoFilter = {
                    from: { row: 3, column: 1 },
                    to: { row: sheet.rowCount, column: totalColumns },
                }
            }
        } catch (error) {
            console.error(`Error processing sheet ${sheet.name}:`, error)
        }
    })

    // Only generate file if there are valid sheets with data
    if (sheets.length > 0) {
        workbook.xlsx
            .writeBuffer()
            .then((data) => {
                const blob = new Blob([data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                })
                const url = window.URL.createObjectURL(blob)
                const anchor = document.createElement('a')
                anchor.href = url
                const formattedDate = formatDate(new Date())
                const fileName = `Ecodesign_[${model || 'Unknown'}]_[${formattedDate}].xlsx`
                anchor.download = fileName
                anchor.click()
                window.URL.revokeObjectURL(url)
            })
            .catch((error) => {
                console.error('Error generating Excel file:', error)
            })
    } else {
        console.warn('No data available to generate Excel file')
    }
}

export default generateExcelFile
